/**
 * Aslen Ops design tokens — single source for web (CSS variables / Tailwind v4)
 * and mobile (NativeWind preset). Values are copied verbatim from design/tokens.md.
 * Do not edit generated/ by hand: run `pnpm --filter @aslen/ui-tokens build`.
 */

export * from './brand';

export type ThemeName = 'light' | 'dark';

export const themeColors = {
  light: {
    bg: '#F7F5F1',
    surface: '#FFFFFF',
    'surface-muted': '#F1EEE8',
    'surface-alt': '#FCFBF9',
    'surface-expanded': '#FAF8F4',
    sidebar: '#F1EEE8',
    border: '#E3DED5',
    'border-strong': '#D3CCC0',
    hairline: '#F5F2EC',
    'hairline-expanded': '#EFEBE3',
    'toast-surface': '#1F1F1D',
    'text-primary': '#1F1F1D',
    'text-secondary': '#5E5B55',
    'text-tertiary': '#8C887F',
    'text-disabled': '#C4C0B8',
    accent: '#B8933F',
    'accent-hover': '#A17F33',
    'accent-tint': '#F5EFE0',
    'on-accent': '#1F1F1D',
    link: '#8A6D27',
    success: '#2E7D4F',
    'success-tint': '#E9F2ED',
    warning: '#B26A00',
    'warning-tint': '#F7EFE2',
    danger: '#B3261E',
    'danger-tint': '#F8EAE9',
    'danger-text': '#B3261E',
    'warning-text': '#B26A00',
    'success-text': '#2E7D4F',
    'info-text': '#2F5FA8',
    // W-09 variance rows: a wash quiet enough to sit under 14 table rows
    'danger-wash': '#FDFAFA',
    'danger-wash-strong': '#FDF4F3',
    info: '#2F5FA8',
    'info-tint': '#E9EFF7',
    focus: '#2F5FA8',
  },
  dark: {
    bg: '#161513',
    surface: '#1F1E1B',
    'surface-muted': '#26241F',
    'surface-alt': '#232120',
    'surface-expanded': '#232120',
    sidebar: '#1A1917',
    border: '#33302A',
    'border-strong': '#454138',
    hairline: '#2B2924',
    'hairline-expanded': '#33302A',
    'toast-surface': '#3D3A33',
    'text-primary': '#F2F0EC',
    'text-secondary': '#A8A49B',
    'text-tertiary': '#7A766D',
    'text-disabled': '#57534B',
    accent: '#C9A653',
    'accent-hover': '#B8933F',
    'accent-tint': '#2A2317',
    'on-accent': '#1F1F1D',
    link: '#C9A653',
    success: '#2E7D4F',
    'success-tint': '#1C2A22',
    warning: '#B26A00',
    'warning-tint': '#2C2314',
    danger: '#B3261E',
    'danger-tint': '#2E1A18',
    'danger-text': '#F0A79E',
    'warning-text': '#E3B579',
    'success-text': '#8FD3AC',
    'info-text': '#7FA6E0',
    'danger-wash': '#241C1A',
    'danger-wash-strong': '#2E1A18',
    info: '#2F5FA8',
    'info-tint': '#1A2230',
    focus: '#7FA6E0',
  },
} as const;

export type ColorToken = keyof (typeof themeColors)['light'];

/** Kategorik grafik paleti (renk körü güvenli). */
export const chartCategorical = ['#0B6E8F', '#C06A0F', '#2E7D4F', '#7A4E9E', '#B3261E', '#6B6B62'] as const;

/** Karşılama günü sıralı skalası (az → çok) ve eşikler. */
export const coverScale = {
  colors: ['#6E4C18', '#9C6F2C', '#C29142', '#D9B36C', '#E8D3A6', '#F6EFE0'] as const,
  thresholds: [10, 20, 30, 45, 70] as const,
} as const;

/** Standart maliyete göre sapma — lehte → aleyhte, −%15 … +%15. */
export const varianceScale = ['#1C6E4E', '#6BA88A', '#C3DACE', '#EDE9E1', '#E7BFA8', '#C77C57', '#9C3A1E'] as const;

/** Marj renk skalası (W-24). */
export const marginScale = [
  { max: 12, light: { bg: '#F3D9D6', fg: '#8E1F18' }, dark: { bg: '#3A1D1A', fg: '#F0A79E' } },
  { max: 20, light: { bg: '#F7E6CC', fg: '#8A5300' }, dark: { bg: '#332616', fg: '#E3B579' } },
  { max: 28, light: { bg: '#F1EEE8', fg: '#3B3936' }, dark: { bg: '#2A2A25', fg: '#CFCBC2' } },
  { max: 35, light: { bg: '#E4F0E9', fg: '#215C3A' }, dark: { bg: '#1E2E25', fg: '#8FD3AC' } },
  { max: Infinity, light: { bg: '#D3E9DC', fg: '#1A4E30' }, dark: { bg: '#1B3A2A', fg: '#A8E6C2' } },
] as const;

/** Pazar yeri renkleri (sabit) — legend sırası bu dizinin sırasıdır. */
export const marketplaceColors = {
  US: '#2F5FA8',
  UK: '#7A4E9E',
  DE: '#C06A0F',
  FR: '#0B6E8F',
  IT: '#2E7D4F',
  ES: '#B3261E',
  NL: '#B8933F',
  SE: '#5E7A9E',
  PL: '#8C4A55',
  BE: '#6B6B62',
} as const;

/** Peçete renk çipleri. */
export const napkinColors = {
  Beyaz: '#FFFFFF',
  Fildişi: '#F2E8D5',
  Bej: '#D9C7A7',
  Gri: '#9A9A95',
  Altın: '#C9A653',
  Lacivert: '#1E2A4A',
  Siyah: '#1A1A18',
  Terracotta: '#B2603C',
  Kahve: '#6B4A2F',
  'Bebek mavisi': '#AFCBE0',
} as const;

/** Kişi renkleri (etkinlik öğesi, avatar). */
export const personColors = ['#2F5FA8', '#2E7D4F', '#7A4E9E', '#C06A0F', '#0B6E8F', '#6B6B62'] as const;

export const fonts = {
  sans: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
} as const;

/** [px, weight, lineHeight] */
export const typeScale = {
  display: [32, 600, 1.1],
  h1: [24, 600, 1.2],
  h2: [20, 600, 1.3],
  h3: [16, 500, 1.4],
  body: [14, 400, 1.5],
  table: [13, 400, 1.45],
  small: [12, 400, 1.4],
  caption: [11, 500, 1.3],
} as const;

export const spacing = [4, 8, 12, 16, 24, 32, 48, 64] as const;

export const radii = {
  input: 6,
  card: 10,
  'card-mobile': 12,
  sheet: 16,
  pill: 999,
} as const;

export const shadows = {
  popover: '0 1px 2px rgba(31,31,29,.06)',
  overlay: '0 12px 32px -8px rgba(31,31,29,.22)',
  drawer: '-24px 0 60px -20px rgba(31,31,29,.40)',
  /** W-06 docked side panel — quieter than the modal drawer, same in both themes. */
  panel: '-12px 0 32px -18px rgba(31,31,29,.25)',
  scrim: 'rgba(31,31,29,.28)',
} as const;

export const motion = {
  fast: '150ms',
  drawerIn: '200ms',
  drawerOut: '150ms',
  flash: '600ms',
  easing: 'cubic-bezier(0, 0, 0.2, 1)',
} as const;

export const rowHeights = {
  webDense: 36,
  webHeader: 34,
  webComfort: 44,
  webRich: 52,
  mobileRow: 56,
  mobileTarget: 48,
  mobilePrimary: 56,
  keypadKey: 58,
  navItem: 32,
} as const;

/** Karşılama günü değerine göre ısı hücresi rengi (≤20 kutularda beyaz metin). */
export function coverHeat(days: number): { bg: string; fg: string } {
  const t = coverScale.thresholds;
  const c = coverScale.colors;
  if (days <= t[0]) return { bg: c[0], fg: '#FFFFFF' };
  if (days <= t[1]) return { bg: c[1], fg: '#FFFFFF' };
  if (days <= t[2]) return { bg: c[2], fg: '#1F1F1D' };
  if (days <= t[3]) return { bg: c[3], fg: '#1F1F1D' };
  if (days <= t[4]) return { bg: c[4], fg: '#1F1F1D' };
  return { bg: c[5], fg: '#5E5B55' };
}

/** Marj yüzdesine göre hücre rengi. */
export function marginTint(pct: number, theme: ThemeName = 'light'): { bg: string; fg: string } {
  const step = marginScale.find((s) => pct < s.max) ?? marginScale[marginScale.length - 1]!;
  return step[theme];
}

export function personColor(index: number): string {
  return personColors[Math.abs(index) % personColors.length]!;
}
